/**
 * Custom Size Calculator — v7 (Cart Transform, placeholder line)
 *
 * The hidden single-variant "custom-size-order" product is added to the cart
 * with the chosen rug and dimensions as line properties (_custom_size=1,
 * _product_id, Rug, _width_ft, _height_ft at full precision). The private
 * "Karelias Custom Size" app (Cart Transform function) prices the line
 * server-side from the trusted rate map (custom_sizing.rate_map on the
 * placeholder) and shows it in cart / checkout / emails with the rug's title
 * and image, without any variant title. Quantity can be changed (per piece).
 * The displayed price uses the same formula, so they always match.
 * If the rate map doesn't match this rug, the snippet switches to quote mode.
 *
 * Rules (same as the function):
 *  - Rectangular: minimum charge 1 m² (10.76 sq ft), max 900 sq ft.
 *  - Round: minimum = product min_area, max 400 sq ft.
 *  - Max 30 ft per side. Larger sizes switch to "Request a Quote".
 *
 * If the storefront can't see the placeholder product in the visitor's market
 * (e.g. Managed Markets), the calculator switches to quote mode.
 */
(function () {
  'use strict';

  var UNITS = {
    ft: { label: 'ft', toFt: 1, decimals: 1, placeholders: { width: '5', height: '8' } },
    in: { label: 'in', toFt: 1 / 12, decimals: 0, placeholders: { width: '60', height: '96' } },
    cm: { label: 'cm', toFt: 1 / 30.48, decimals: 0, placeholders: { width: '150', height: '245' } }
  };
  var MIN_FT = 1;
  var MAX_FT = 30;
  var STORAGE_KEY = 'custom-size-unit';
  var DIMS_STORAGE_KEY = 'custom-size-dims';

  function activeCurrency(container) {
    var s = window.Shopify || {};
    return (s.currency && s.currency.active) || container.dataset.currency || 'USD';
  }

  // Store prices are in USD; convert with the storefront's presentment rate
  // (same rate the Cart Transform function uses).
  function fxRate() {
    var s = window.Shopify || {};
    var r = s.currency && parseFloat(s.currency.rate);
    return r > 0 ? r : 1;
  }

  function usdToCents(usd) {
    return Math.round(usd * fxRate() * 100);
  }

  function formatMoney(cents, currency) {
    var amount = cents / 100;
    try {
      var locale = (window.Shopify && window.Shopify.locale) || document.documentElement.lang || 'en';
      return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(amount);
    } catch (e) {
      return amount.toFixed(2) + ' ' + currency;
    }
  }

  function convertValue(value, fromUnit, toUnit) {
    if (fromUnit === toUnit || isNaN(value)) return value;
    return (value * UNITS[fromUnit].toFt) / UNITS[toUnit].toFt;
  }

  function formatDimValue(value, unit) {
    return parseFloat(value.toFixed(UNITS[unit].decimals));
  }

  function getConversionHint(valueFt, currentUnit) {
    if (isNaN(valueFt) || valueFt <= 0) return '';
    var hints = [];
    if (currentUnit !== 'ft') hints.push(formatDimValue(valueFt, 'ft') + ' ft');
    if (currentUnit !== 'in') hints.push(formatDimValue(valueFt / UNITS.in.toFt, 'in') + ' in');
    if (currentUnit !== 'cm') hints.push(formatDimValue(valueFt / UNITS.cm.toFt, 'cm') + ' cm');
    return hints.join(' \u00b7 ');
  }

  function store(key, val) { try { localStorage.setItem(key, val); } catch (e) { /* noop */ } }
  function read(key) { try { return localStorage.getItem(key); } catch (e) { return null; } }

  function getDefaultUnit() {
    var saved = read(STORAGE_KEY);
    return saved && UNITS[saved] ? saved : 'ft';
  }

  function getSavedDims() {
    try {
      var d = JSON.parse(read(DIMS_STORAGE_KEY) || 'null');
      if (d && d.w > 0 && d.h > 0 && UNITS[d.u]) return d;
    } catch (e) { /* noop */ }
    return null;
  }

  function debounce(fn, delay) {
    var t;
    return function () {
      var a = arguments, s = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(s, a); }, delay);
    };
  }

  // "1 m² (10.8 sq ft)" when the area is a whole number of m², else "16.0 sq ft".
  function areaLabel(sqft) {
    var m2 = sqft / 10.7639;
    if (Math.abs(m2 - Math.round(m2)) < 0.01) return Math.round(m2) + ' m\u00b2 (' + sqft.toFixed(1) + ' sq ft)';
    return sqft.toFixed(1) + ' sq ft';
  }

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function CustomSizeCalculator(container) {
    var d = container.dataset;
    this.container = container;
    this.pricePerSqft = parseFloat(d.pricePerSqft) || 0;
    this.shape = d.shape || 'rectangular';
    this.minArea = parseFloat(d.minArea) || 0;
    this.maxArea = parseFloat(d.maxArea) || 0;
    this.productId = d.productId;
    this.productTitle = d.productTitle;
    this.productUrl = d.productUrl;
    this.variantId = d.variantId;
    this.orderVariantId = parseInt(d.orderVariantId, 10) || 0;
    this.imageUrl = d.imageUrl;
    this.quoteMode = d.quoteMode !== 'false';
    this.quoteEmail = d.quoteEmail || 'info@kareliasandsons.com';
    this.currentUnit = getDefaultUnit();
    this.quote = null;

    try { this.variantSizes = JSON.parse(d.variantSizes || '[]'); } catch (e) { this.variantSizes = []; }

    this._cacheDom();
    this._defaultSubmitText = this.submitText ? this.submitText.textContent : '';
    this._bindEvents();
    this._applyUnit(this.currentUnit, false);
    this._restoreSavedDims();
  }

  var P = CustomSizeCalculator.prototype;

  P._cacheDom = function () {
    var c = this.container;
    this.dimsDisplay = c.querySelector('.custom-size__dims-display');
    this.rateDisplay = c.querySelector('.custom-size__rate-display');
    this.priceDisplay = c.querySelector('.custom-size__price-value');
    this.submitBtn = c.querySelector('.custom-size__submit');
    this.submitText = c.querySelector('.custom-size__submit-text');
    this.spinner = c.querySelector('.hdt-loading__spinner');
    this.errorEl = c.querySelector('.custom-size__error');
    this.closeBtn = c.querySelector('.custom-size__close');
    this.inputs = c.querySelectorAll('.custom-size__input');
    this.unitBtns = c.querySelectorAll('.custom-size__unit-btn');
    this.unitLabels = c.querySelectorAll('.custom-size__unit-label');
    this.convertHints = c.querySelectorAll('.custom-size__convert-hint');
    this.noteEl = c.querySelector('.custom-size__min-note');
    if (!this.noteEl && this.errorEl) {
      this.noteEl = document.createElement('div');
      this.noteEl.className = 'custom-size__min-note hdt-s-text';
      this.noteEl.hidden = true;
      this.noteEl.style.cssText = 'margin:8px 0;padding:10px 14px;border-radius:6px;background:#f6f3ee;color:#5a4a32;';
      this.errorEl.parentNode.insertBefore(this.noteEl, this.errorEl.nextSibling);
    }
  };

  P._bindEvents = function () {
    var self = this;
    var calc = debounce(function () { self.calculate(); self._saveDims(); }, 150);
    this.inputs.forEach(function (input) {
      input.addEventListener('input', calc);
      input.addEventListener('change', function () { self.calculate(); self._saveDims(); });
    });
    this.submitBtn.addEventListener('click', function () { self.submit(); });
    if (this.closeBtn) this.closeBtn.addEventListener('click', function () { self.hide(); });
    this.unitBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.dataset.unit !== self.currentUnit) self._switchUnit(btn.dataset.unit);
      });
    });
  };

  P._switchUnit = function (newUnit) {
    var oldUnit = this.currentUnit;
    this.inputs.forEach(function (input) {
      var v = parseFloat(input.value);
      if (!isNaN(v) && v > 0) input.value = formatDimValue(convertValue(v, oldUnit, newUnit), newUnit);
    });
    this._applyUnit(newUnit, true);
    this.calculate();
  };

  P._applyUnit = function (unit, save) {
    this.currentUnit = unit;
    if (save) store(STORAGE_KEY, unit);
    var def = UNITS[unit];
    this.unitBtns.forEach(function (btn) {
      var on = btn.dataset.unit === unit;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    this.unitLabels.forEach(function (l) { l.textContent = def.label; });
    this.inputs.forEach(function (input) {
      var dim = input.dataset.dimension;
      if (dim && def.placeholders[dim]) input.placeholder = 'e.g. ' + def.placeholders[dim];
    });
    this._updateRateDisplay();
  };

  P._updateRateDisplay = function () {
    if (!this.rateDisplay) return;
    if (this.quoteMode || !this.pricePerSqft) { this.rateDisplay.textContent = ''; return; }
    this.rateDisplay.textContent = formatMoney(usdToCents(this.pricePerSqft), activeCurrency(this.container)) + ' / sq ft';
  };

  P.show = function () {
    this.container.hidden = false;
    this._updateRateDisplay();
    var first = this.container.querySelector('.custom-size__input');
    if (first) setTimeout(function () { first.focus(); }, 100);
  };

  P.hide = function () {
    this.container.hidden = true;
    this.clearError();
    var btn = document.querySelector('.custom-size__trigger.is-active');
    if (btn) {
      btn.classList.remove('is-active');
      btn.setAttribute('aria-expanded', 'false');
    }
  };

  P._inputsWH = function () {
    return {
      w: this.container.querySelector('[data-dimension="width"]'),
      h: this.container.querySelector('[data-dimension="height"]')
    };
  };

  P._saveDims = function () {
    var i = this._inputsWH();
    var w = parseFloat(i.w && i.w.value), h = parseFloat(i.h && i.h.value);
    if (w > 0 && h > 0) store(DIMS_STORAGE_KEY, JSON.stringify({ w: w, h: h, u: this.currentUnit }));
  };

  P._restoreSavedDims = function () {
    var s = getSavedDims();
    var i = this._inputsWH();
    if (!s || !i.w || !i.h) return;
    i.w.value = formatDimValue(convertValue(s.w, s.u, this.currentUnit), this.currentUnit);
    i.h.value = formatDimValue(convertValue(s.h, s.u, this.currentUnit), this.currentUnit);
    this.calculate();
  };

  P._getDimensionsFt = function () {
    var i = this._inputsWH();
    var w = parseFloat(i.w && i.w.value), h = parseFloat(i.h && i.h.value);
    if (isNaN(w) || isNaN(h) || w <= 0 || h <= 0) return { valid: false };
    var f = UNITS[this.currentUnit].toFt;
    return { valid: true, width: w * f, height: h * f };
  };

  P._matchesExistingVariant = function (wFt, hFt) {
    for (var i = 0; i < this.variantSizes.length; i++) {
      var v = this.variantSizes[i];
      if (Math.abs(v.w - wFt) <= 0.05 && Math.abs(v.h - hFt) <= 0.05) return true;
    }
    return false;
  };

  P._setNote = function (msg) {
    if (!this.noteEl) return;
    this.noteEl.textContent = msg || '';
    this.noteEl.hidden = !msg;
  };

  P._setSubmitLabel = function (quote) {
    if (!this.submitText) return;
    this.submitText.textContent = quote ? 'Request a Quote' : this._defaultSubmitText;
  };

  P._fail = function (msg, dimsStr) {
    this.quote = null;
    if (this.dimsDisplay) this.dimsDisplay.textContent = dimsStr || '';
    this.priceDisplay.textContent = '\u2014';
    this.submitBtn.disabled = true;
    this._setNote('');
    this._setSubmitLabel(this.quoteMode);
    if (msg) this.showError(msg); else this.clearError();
    this._updateConversionHints();
  };

  P.calculate = function () {
    var d = this._getDimensionsFt();
    if (!d.valid) return this._fail(null);

    if (!this.quoteMode && (!this.orderVariantId || !this.pricePerSqft)) {
      return this._fail('Custom sizing is temporarily unavailable. Please contact us for a quote.');
    }
    if (d.width < MIN_FT || d.height < MIN_FT) {
      return this._fail('Minimum dimension is 1 foot (12 in / 30 cm).');
    }
    if (this.shape === 'round' && Math.round(d.width * 10) !== Math.round(d.height * 10)) {
      return this._fail('Round rugs require equal width and height (e.g., 5\u00d75, 6\u00d76, 9\u00d79).');
    }

    var area = d.width * d.height;
    var dimsStr = (Math.round(d.width * 10) / 10) + "' \u00d7 " + (Math.round(d.height * 10) / 10) +
      "' = " + area.toFixed(1) + ' sq ft';

    if (this._matchesExistingVariant(d.width, d.height)) {
      return this._fail('This size is available as a standard option. Please select it from the size options above.', dimsStr);
    }

    // Too large to price online: offer a quote instead of a dead end.
    var tooLong = d.width > MAX_FT || d.height > MAX_FT;
    var tooBig = this.maxArea > 0 && area > this.maxArea;
    if (tooLong || tooBig) {
      this.quote = { usd: 0, area: area, billedArea: area, dims: d, overMax: true };
      if (this.dimsDisplay) this.dimsDisplay.textContent = dimsStr;
      this.priceDisplay.textContent = 'On request';
      this.submitBtn.disabled = false;
      this.clearError();
      this._setNote('This is a large piece. Send us a quick quote request and we will reply with your price and delivery options within 24 hours.');
      this._setSubmitLabel(true);
      this._updateConversionHints();
      return;
    }

    // Below the minimum: bill as the minimum area instead of blocking.
    var billedArea = area;
    var minNote = '';
    if (this.minArea > 0 && area < this.minArea) {
      billedArea = this.minArea;
      minNote = 'Minimum charge for custom sizes: priced as ' + areaLabel(this.minArea) + '.';
    }

    var usd = Math.ceil(billedArea * this.pricePerSqft - 1e-9);
    this.quote = { usd: usd, area: area, billedArea: billedArea, dims: d, overMax: false };

    if (this.dimsDisplay) this.dimsDisplay.textContent = dimsStr;
    this.priceDisplay.textContent = this.quoteMode
      ? 'On request'
      : formatMoney(usdToCents(usd), activeCurrency(this.container));
    this.submitBtn.disabled = false;
    this.clearError();
    this._setNote(minNote);
    this._setSubmitLabel(this.quoteMode);
    this._updateConversionHints();
  };

  P._updateConversionHints = function () {
    var self = this, f = UNITS[this.currentUnit].toFt;
    this.inputs.forEach(function (input, i) {
      var hint = self.convertHints[i];
      if (!hint) return;
      var v = parseFloat(input.value);
      var text = (!isNaN(v) && v > 0) ? getConversionHint(v * f, self.currentUnit) : '';
      hint.textContent = text;
      hint.hidden = !text;
    });
  };

  P.showError = function (msg) { this.errorEl.textContent = msg; this.errorEl.hidden = false; };
  P.clearError = function () { this.errorEl.textContent = ''; this.errorEl.hidden = true; };

  P.setLoading = function (on) {
    this.submitBtn.disabled = on;
    this.submitText.style.visibility = on ? 'hidden' : 'visible';
    if (this.spinner) this.spinner.hidden = !on;
  };

  P.submit = function () {
    var self = this;
    this.calculate();
    var q = this.quote;
    if (!q) return;

    var wFt = q.dims.width, hFt = q.dims.height;
    var wCm = Math.round(wFt * 30.48), hCm = Math.round(hFt * 30.48);
    var sizeLabel = self.shape === 'round'
      ? (Math.round(wFt * 10) / 10) + "' diameter (" + wCm + ' cm)'
      : (Math.round(wFt * 10) / 10) + "' \u00d7 " + (Math.round(hFt * 10) / 10) + "' (" + wCm + ' \u00d7 ' + hCm + ' cm)';

    if (this.quoteMode || q.overMax) return this._requestQuote(sizeLabel, q.area);

    // Full precision dimensions: the Cart Transform function recalculates the
    // price from these, so it must see exactly what the calculator used.
    var properties = {
      'Rug': this.productTitle,
      'Size': sizeLabel,
      'Area': q.area.toFixed(1) + ' sq ft',
      'Shape': this.shape === 'round' ? 'Round' : 'Rectangular',
      '_custom_size': '1',
      '_product_id': String(this.productId),
      '_width_ft': String(wFt),
      '_height_ft': String(hFt),
      '_image': this.imageUrl,
      '_product_url': this.productUrl,
      '_uid': Date.now().toString(36) + Math.random().toString(36).slice(2, 7)
    };
    if (q.billedArea > q.area) {
      properties['_billed_area'] = String(q.billedArea);
    }

    this.setLoading(true);
    this.clearError();

    fetch(rootUrl() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: this.orderVariantId, quantity: 1, properties: properties }] })
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart');
          return data;
        });
      })
      .then(function (data) {
        var line = data.items && data.items[0];
        if (!line || line.quantity !== 1) {
          throw new Error('Your custom size could not be added correctly. Please contact us.');
        }
        window.location.href = rootUrl() + 'cart';
      })
      .catch(function (err) {
        console.error('Custom size error:', err);
        self.showError(err.message || 'Something went wrong. Please try again.');
        self.setLoading(false);
      });
  };

  P._requestQuote = function (sizeLabel, area) {
    var s = window.Shopify || {};
    var subject = 'Custom size quote: ' + this.productTitle;
    var body = [
      'Hello Karelias And Sons,',
      '',
      'I would like a quote for a custom size rug.',
      '',
      'Rug: ' + this.productTitle,
      'Link: ' + window.location.origin + this.productUrl,
      'Size: ' + sizeLabel,
      'Area: ' + area.toFixed(1) + ' sq ft',
      'Shape: ' + (this.shape === 'round' ? 'Round' : 'Rectangular'),
      'Country: ' + (s.country || ''),
      'Currency: ' + activeCurrency(this.container),
      '',
      'Name:',
      'Delivery address / postcode:',
      ''
    ].join('\n');
    window.location.href = 'mailto:' + this.quoteEmail +
      '?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(body);
  };

  function init() {
    document.querySelectorAll('.custom-size__trigger').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var calc = document.getElementById('custom-size-calculator-' + btn.dataset.sectionId);
        if (!calc) return;

        if (btn.classList.contains('is-active')) {
          btn.classList.remove('is-active');
          btn.setAttribute('aria-expanded', 'false');
          calc.hidden = true;
          return;
        }
        btn.classList.add('is-active');
        btn.setAttribute('aria-expanded', 'true');

        var picker = btn.closest('hdt-variant-picker') || btn.closest('.hdt-variant-picker');
        var fs = picker && picker.querySelector('.is-style-size:not(:last-child)');
        var checked = fs && fs.querySelector('input[type="radio"]:checked');
        if (checked) {
          checked.checked = false;
          var label = fs.querySelector('label[for="' + checked.id + '"]');
          if (label) label.classList.remove('is-checked');
        }

        if (!calc._instance) calc._instance = new CustomSizeCalculator(calc);
        calc._instance.show();
      });
    });

    document.querySelectorAll('hdt-variant-picker input[type="radio"]').forEach(function (radio) {
      radio.addEventListener('change', function () {
        var picker = radio.closest('hdt-variant-picker');
        var trigger = picker && picker.querySelector('.custom-size__trigger.is-active');
        if (!trigger) return;
        trigger.classList.remove('is-active');
        trigger.setAttribute('aria-expanded', 'false');
        var calc = document.getElementById('custom-size-calculator-' + trigger.dataset.sectionId);
        if (calc) calc.hidden = true;
      });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();